import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";

const FeeSummaryReport = () => {
  const storedSession = localStorage.getItem("currentSessionId") || "";
  const [currentSession] = useState(storedSession);

  const months = [
    "April", "May", "June", "July", "August", "September",
    "October", "November", "December", "January", "February", "March"
  ];

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);

  const [selectedMonth, setSelectedMonth] = useState("");
const [mode, setMode] = useState("single");
  // ✅ ADDED
  const [fromMonth, setFromMonth] = useState("");
  const [toMonth, setToMonth] = useState("");

  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const monthKeyMap = {
    "April": "month1", "May": "month2", "June": "month3", "July": "month4",
    "August": "month5", "September": "month6", "October": "month7", "November": "month8",
    "December": "month9", "January": "month10", "February": "month11", "March": "month12"
  };

  const busKeyMap = {
    "April": "route_month1", "May": "route_month2", "June": "route_month3", "July": "route_month4",
    "August": "route_month5", "September": "route_month6", "October": "route_month7", "November": "route_month8",
    "December": "route_month9", "January": "route_month10", "February": "route_month11", "March": "route_month12"
  };

  useEffect(() => {
    if (!currentSession) return;
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses`, { params: { sessionId: currentSession } })
      .then(res => setClasses(res.data || []))
      .catch(console.error);
  }, [currentSession]);

  useEffect(() => {
    if (!selectedClass) {
      setSections([]);
      setSelectedSection("");
      return;
    }
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(selectedClass)}/getSectionsByClassName`, {
      params: { sessionId: currentSession }
    })
      .then(res => setSections(res.data || []))
      .catch(console.error);
  }, [selectedClass, currentSession]);

  // ✅ UPDATED ONLY LOGIC PART
  const fetchData = async () => {
    if (!selectedMonth && !(fromMonth && toMonth)) {
      setError("Please select a month OR from-to months.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const params = {
        sessionId: currentSession,
        className: selectedClass || undefined,
        sectionName: selectedSection || undefined
      };

      // ✅ condition logic
      if (fromMonth && toMonth) {
        params.fromMonth = fromMonth;
        params.toMonth = toMonth;
      } else {
        params.month = selectedMonth;
      }

      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/fees/report/summary`, {
        params
      });

      setStudents(response.data || []);
    } catch (err) {
      setError("Error fetching data.");
    } finally {
      setLoading(false);
    }
  };

  const totalExpected = students.reduce((sum, s) => sum + (s.totalExpected || 0), 0);
  const totalPaid = students.reduce((sum, s) => sum + (s.totalPaid || 0), 0);
  const totalPending = students.reduce((sum, s) => sum + (s.totalPending || 0), 0);

  const tuitionKey = monthKeyMap[selectedMonth] || null;
  const busKey = busKeyMap[selectedMonth] || null;

  const exportToExcel = () => {
    if (!students.length) return;

    const data = students.map(s => {
      let tuition = 0;
let bus = 0;

if (fromMonth && toMonth) {
  Object.keys(s.feeHeads || {}).forEach(key => {
    if (key.startsWith("month")) {
      tuition += s.feeHeads[key]?.expected || 0;
    }
    if (key.startsWith("route_month")) {
      bus += s.feeHeads[key]?.expected || 0;
    }
  });
} else {
  tuition = tuitionKey && s.feeHeads?.[tuitionKey]?.expected || 0;
  bus = busKey && s.feeHeads?.[busKey]?.expected || 0;
}
      return {
        "Admission No": s.admissionNo || "-",
        "Name": s.name || "-",
        "Class": s.class || "-",
        "Section": s.section || "-",
        "Expected Tuition": tuition,
        "Expected Bus": bus,
        "Total Expected": s.totalExpected || 0,
        "Total Paid": s.totalPaid || 0,
        "Pending": s.totalPending || 0
      };
    });

    data.push({
      "Admission No": "",
      "Name": "Total",
      "Class": "",
      "Section": "",
      "Expected Tuition": students.reduce((sum, s) => sum + (tuitionKey && s.feeHeads?.[tuitionKey]?.expected || 0), 0),
      "Expected Bus": students.reduce((sum, s) => sum + (busKey && s.feeHeads?.[busKey]?.expected || 0), 0),
      "Total Expected": totalExpected,
      "Total Paid": totalPaid,
      "Pending": totalPending
    });

    const ws = XLSX.utils.json_to_sheet(data, { origin: "A1" });

    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
      if (!ws[cellAddress]) continue;
      ws[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4F81BD" } },
        alignment: { horizontal: "center" }
      };
    }

    const totalsRow = range.e.r;
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cellAddress = XLSX.utils.encode_cell({ r: totalsRow, c: C });
      if (!ws[cellAddress]) continue;
      ws[cellAddress].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "D9D9D9" } },
        alignment: { horizontal: "center" }
      };
    }

    ws['!cols'] = [
      { wch: 15 },
      { wch: 25 },
      { wch: 10 },
      { wch: 10 },
      { wch: 18 },
      { wch: 15 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 }
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "FeeSummary");

    XLSX.writeFile(wb, `FeeSummary_${selectedMonth || "Report"}.xlsx`);
  };

  return (
    <div className="p-6 max-w-full">
      <h2 className="text-xl font-semibold mb-4">Fee Summary Report</h2>
<button
  onClick={() => setMode("single")}
  className={`px-3 py-2 rounded ${
    mode === "single"
      ? "bg-blue-600 text-white"
      : "bg-gray-200 text-black"
  }`}
>
  Single Month
</button>

<button
  onClick={() => setMode("range")}
  className={`px-3 py-2 rounded ${
    mode === "range"
      ? "bg-purple-600 text-white"
      : "bg-gray-200 text-black"
  }`}
>
  From-To Month
</button>
      <div className="flex gap-4 mb-6 flex-wrap mt-4">

       {mode === "single" && (
  <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="border px-3 py-2 rounded">
    <option value="">Select Month</option>
    {months.map(m => <option key={m} value={m}>{m}</option>)}
  </select>
)}

{mode === "range" && (
  <>
    <select value={fromMonth} onChange={e => setFromMonth(e.target.value)} className="border px-3 py-2 rounded">
      <option value="">From Month</option>
      {months.map(m => <option key={m} value={m}>{m}</option>)}
    </select>

    <select value={toMonth} onChange={e => setToMonth(e.target.value)} className="border px-3 py-2 rounded">
      <option value="">To Month</option>
      {months.map(m => <option key={m} value={m}>{m}</option>)}
    </select>
  </>
)}

        <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)} className="border px-3 py-2 rounded">
          <option value="">All Classes</option>
          {classes.map(c => <option key={c._id} value={c.className}>{c.className}</option>)}
        </select>

        <select value={selectedSection} onChange={e => setSelectedSection(e.target.value)} disabled={!selectedClass} className="border px-3 py-2 rounded">
          <option value="">All Sections</option>
          {sections.map(s => <option key={s._id} value={s.sectionName}>{s.sectionName}</option>)}
        </select>

        <button onClick={fetchData} disabled={loading} className="bg-blue-600 text-white px-4 py-2 rounded">
          {loading ? "Loading..." : "Search"}
        </button>

        {students.length > 0 && (
          <button onClick={exportToExcel} className="bg-green-600 text-white px-4 py-2 rounded">
            Export to Excel
          </button>
        )}
      </div>

      {error && <div className="text-red-600 mb-4">{error}</div>}

      {students.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-100 p-4 rounded shadow text-center">
            <p className="text-sm text-gray-600">Total Expected</p>
            <p className="text-lg font-bold">{totalExpected}</p>
          </div>
          <div className="bg-green-100 p-4 rounded shadow text-center">
            <p className="text-sm text-gray-600">Total Received</p>
            <p className="text-lg font-bold text-green-700">{totalPaid}</p>
          </div>
          <div className="bg-red-100 p-4 rounded shadow text-center">
            <p className="text-sm text-gray-600">Total Pending</p>
            <p className="text-lg font-bold text-red-700">{totalPending}</p>
          </div>
        </div>
      )}

      <div className="overflow-auto border rounded max-h-[600px] max-w-full" style={{ minWidth: "900px" }}>
        <table className="min-w-full border-collapse border text-center">
          <thead className="bg-gray-200 sticky top-0 z-10">
            <tr>
              <th className="border px-3 py-2">Admission No</th>
              <th className="border px-3 py-2">Student Name</th>
              <th className="border px-3 py-2">Expected Tuition</th>
              <th className="border px-3 py-2">Expected Bus</th>
              <th className="border px-3 py-2">Actual Paid</th>
              <th className="border px-3 py-2">Pending</th>
            </tr>
          </thead>
          <tbody>
            {students.map((s, idx) => {
              let expectedTuition = 0;
let expectedBus = 0;

if (fromMonth && toMonth) {
  Object.keys(s.feeHeads || {}).forEach(key => {
    if (key.startsWith("month")) {
      expectedTuition += s.feeHeads[key]?.expected || 0;
    }
    if (key.startsWith("route_month")) {
      expectedBus += s.feeHeads[key]?.expected || 0;
    }
  });
} else {
  expectedTuition = tuitionKey && s.feeHeads?.[tuitionKey]?.expected || 0;
  expectedBus = busKey && s.feeHeads?.[busKey]?.expected || 0;
}
              const pending = (s.totalExpected || 0) - (s.totalPaid || 0);

              return (
                <tr key={idx} className={pending > 0 ? "text-red-600 font-semibold" : ""}>
                  <td className="border px-3 py-2">{s.admissionNo || "-"}</td>
                  <td className="border px-3 py-2">{s.name || "-"}</td>
                  <td className="border px-3 py-2">{expectedTuition}</td>
                  <td className="border px-3 py-2">{expectedBus}</td>
                  <td className="border px-3 py-2">{s.totalPaid || 0}</td>
                  <td className="border px-3 py-2">{pending > 0 ? pending : 0}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FeeSummaryReport;